'use client';

import { c as _c } from "react/compiler-runtime";
import { AlertCircle, CheckCircle, Clock, Database, FileCode, FileText, GitBranch, Loader2, Lock, Package, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WikiRetryButton } from '@/components/wiki/wiki-retry-button';
import { useWikiGenerationProgress } from '@/data/wiki/use-wiki-progress';
import { WikiGenerationStage } from '@/generated/prisma/enums';
import { decodeHtmlEntities } from '@/lib/html-decode';
import { cn } from '@/lib/utils';

// ==================== Shared Utilities ====================

export const getWikiStageIcon = (stage: string | WikiGenerationStage) => {
  const iconClass = 'h-4 w-4';
  switch (stage) {
    case WikiGenerationStage.starting:
    case WikiGenerationStage.cloning:
      return <GitBranch className={cn(iconClass, 'text-muted-foreground')} />;
    case WikiGenerationStage.processing:
      return <FileText className={cn(iconClass, 'text-muted-foreground')} />;
    case WikiGenerationStage.ai_structure:
    case WikiGenerationStage.parsing:
      return <Sparkles className={cn(iconClass, 'text-muted-foreground')} />;
    case WikiGenerationStage.pages:
    case WikiGenerationStage.page_generation:
      return <Loader2 className={cn(iconClass, 'animate-spin text-primary')} />;
    case WikiGenerationStage.saving:
      return <Database className={cn(iconClass, 'text-muted-foreground')} />;
    case WikiGenerationStage.completed:
      return <CheckCircle className={cn(iconClass, 'text-emerald-500')} />;
    case WikiGenerationStage.error:
      return <AlertCircle className={cn(iconClass, 'text-destructive')} />;
    default:
      return <Loader2 className={cn(iconClass, 'animate-spin text-muted-foreground')} />;
  }
};
export const getWikiStageColor = (stage: string | WikiGenerationStage): string => {
  switch (stage) {
    case WikiGenerationStage.completed:
      return 'text-emerald-600';
    case WikiGenerationStage.error:
      return 'text-destructive';
    case WikiGenerationStage.pages:
    case WikiGenerationStage.page_generation:
      return 'text-primary';
    default:
      return 'text-muted-foreground';
  }
};
export const formatTimeRemaining = (ms: number): string => {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.round(seconds / 60);
  return `${minutes}m`;
};

// ==================== Shared Components ====================

type ProgressIconProps = {
  stage?: string;
  className?: string;
};
export function ProgressIcon(t0) {
  const $ = _c(10);
  const {
    stage,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative inline-block", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="absolute -inset-2 bg-primary/5 rounded-full blur-md" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = stage || "";
  let t4;
  if ($[3] !== t3) {
    t4 = getWikiStageIcon(t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <div className="relative flex items-center justify-center w-10 h-10 bg-background rounded-full border border-border/50">{t4}</div>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t5) {
    t6 = <div className={t1}>{t2}{t5}</div>;
    $[7] = t1;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
type ProgressBarProps = {
  percentage: number;
  className?: string;
  height?: 'sm' | 'md';
};
export function ProgressBar(t0) {
  const $ = _c(8);
  const {
    percentage,
    className,
    height: t1
  } = t0;
  const height = t1 === undefined ? "md" : t1;
  const heightClass = height === "sm" ? "h-0.5" : "h-1";
  let t2;
  if ($[0] !== className || $[1] !== heightClass) {
    t2 = cn("w-full bg-muted/30 rounded-full overflow-hidden", heightClass, className);
    $[0] = className;
    $[1] = heightClass;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `${Math.max(2, Math.min(100, percentage))}%`;
  let t4;
  if ($[3] !== t3) {
    t4 = <div className="bg-gradient-to-r from-primary/80 to-primary h-full rounded-full transition-all duration-700 ease-out" style={{
      width: t3
    }} />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <div className={t2}>{t4}</div>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
type ProgressTextProps = {
  message: string;
  percentage?: number | null;
  className?: string;
  size?: 'sm' | 'md';
};
export function ProgressText(t0) {
  const $ = _c(12);
  const {
    message,
    percentage,
    className,
    size: t1
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const textSize = size === "sm" ? "text-xs" : "text-sm";
  const percentSize = size === "sm" ? "text-[10px]" : "text-xs";
  let t2;
  if ($[0] !== textSize) {
    t2 = cn(textSize, "font-medium text-foreground/90");
    $[0] = textSize;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== message || $[3] !== t2) {
    t3 = <h3 className={t2}>{message}</h3>;
    $[2] = message;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== percentSize || $[6] !== percentage) {
    t4 = percentage !== null && percentage !== undefined && <p className={cn(percentSize, "text-muted-foreground mt-1")}>{percentage}% complete</p>;
    $[5] = percentSize;
    $[6] = percentage;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== className || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className={className}>{t3}{t4}</div>;
    $[8] = className;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

// ==================== Wiki State Components ====================

export function WikiProgressLoading() {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ProgressIcon stage={WikiGenerationStage.starting} className="mb-3" />;
    t1 = <ProgressText message="Loading progress..." percentage={null} />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="w-full max-w-sm mx-auto space-y-4"><div className="text-center space-y-3">{t0}{t1}<div className="w-full bg-muted/30 rounded-full overflow-hidden h-1"><div className="bg-gradient-to-r from-primary/30 to-primary/30 h-full rounded-full animate-pulse" /></div></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
type WikiLoadingStateProps = {
  message?: string;
  submessage?: string;
};
export function WikiLoadingState(t0) {
  const $ = _c(9);
  const {
    message: t1,
    submessage: t2
  } = t0;
  const message = t1 === undefined ? "Loading wiki" : t1;
  const submessage = t2 === undefined ? "Please wait while we fetch your documentation." : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute -inset-2 bg-primary/5 rounded-full blur-md" />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="relative inline-block mb-3">{t3}<div className="relative flex items-center justify-center w-10 h-10 bg-background rounded-full border border-border/50"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div></div>;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== message) {
    t5 = <ProgressText message={message} percentage={null} />;
    $[2] = message;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== submessage) {
    t6 = submessage && <p className="text-xs text-muted-foreground mt-2 max-w-xs mx-auto">{submessage}</p>;
    $[4] = submessage;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = <div className="flex flex-col items-center justify-center min-h-[60vh] w-full"><div className="w-full max-w-sm mx-auto"><div className="space-y-3"><div className="text-center">{t4}{t5}{t6}</div></div></div></div>;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
}
type WikiEmptyStateProps = {
  onGenerate?: () => void;
  onUpgrade?: () => void;
  isGenerating?: boolean;
  isLoading?: boolean;
  requiresUpgrade?: boolean;
  isPrivateRepo?: boolean;
};
export function WikiEmptyState(t0) {
  const $ = _c(21);
  const {
    onGenerate,
    onUpgrade,
    isGenerating,
    isLoading,
    requiresUpgrade: t1,
    isPrivateRepo: t2
  } = t0;
  const requiresUpgrade = t1 === undefined ? false : t1;
  const isPrivateRepo = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute -inset-2 bg-primary/5 rounded-full blur-md" />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== requiresUpgrade) {
    t4 = <div className="relative inline-block mb-3">{t3}<div className="relative flex items-center justify-center w-10 h-10 bg-background rounded-full border border-border/50">{requiresUpgrade ? <Lock className="h-4 w-4 text-muted-foreground" /> : <FileCode className="h-4 w-4 text-muted-foreground" />}</div></div>;
    $[1] = requiresUpgrade;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = requiresUpgrade ? "You must be on a paid plan to generate a wiki" : "This wiki doesn't exist yet";
  let t6;
  if ($[3] !== t5) {
    t6 = <h3 className="text-base font-medium text-foreground/90">{t5}</h3>;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = requiresUpgrade ? "Upgrade to generate documentation." : "Generate comprehensive documentation for this repository to help your team understand the codebase better.";
  let t8;
  if ($[5] !== t7) {
    t8 = <p className="text-base text-muted-foreground mt-2 max-w-xs mx-auto">{t7}</p>;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== t4 || $[8] !== t6 || $[9] !== t8) {
    t9 = <div className="text-center">{t4}{t6}{t8}</div>;
    $[7] = t4;
    $[8] = t6;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== isGenerating || $[12] !== isLoading || $[13] !== isPrivateRepo || $[14] !== onGenerate || $[15] !== onUpgrade || $[16] !== requiresUpgrade) {
    t10 = requiresUpgrade && onUpgrade ? <div className="text-center mt-6"><Button onClick={onUpgrade} disabled={isLoading} size="default" className="px-4"><Lock className="mr-2 h-4 w-4" />View plans</Button></div> : onGenerate ? <div className="text-center mt-6"><Button onClick={onGenerate} disabled={isLoading || isGenerating} size="default" className="px-4"><Package className="mr-2 h-4 w-4" />Generate wiki</Button>{isPrivateRepo ? <p className="mt-3 text-xs text-muted-foreground">This uses one private wiki slot. You can reassign the slot 90 days after the first successful generation finishes by deleting this wiki.</p> : null}</div> : null;
    $[11] = isGenerating;
    $[12] = isLoading;
    $[13] = isPrivateRepo;
    $[14] = onGenerate;
    $[15] = onUpgrade;
    $[16] = requiresUpgrade;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <div className="w-full max-w-sm mx-auto"><div className="space-y-3">{t9}{t10}</div></div>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}

// ==================== Wiki Progress Display ====================

type WikiProgressDisplayProps = {
  wikiId?: string | null;
  onRetry?: () => void;
  isGenerating?: boolean;
};
export function WikiProgressDisplay(t0) {
  const $ = _c(8);
  const {
    wikiId,
    onRetry,
    isGenerating: t1
  } = t0;
  const isGenerating = t1 === undefined ? false : t1;
  const {
    data: wikiProgress,
    isLoading
  } = useWikiGenerationProgress(wikiId || undefined, !!wikiId && isGenerating);
  const currentProgress = wikiProgress && wikiProgress.length > 0 ? wikiProgress[0] : null;
  if (isLoading) {
    return null;
  }
  if (!wikiId || !currentProgress && !isGenerating) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="w-full max-w-md mx-auto"><div className="text-center space-y-4"><div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center"><FileText className="h-8 w-8 text-muted-foreground" /></div><div><h3 className="text-xl font-semibold">No generation in progress</h3><p className="text-muted-foreground mt-2">Start wiki generation to see progress updates here.</p></div></div></div>;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] !== currentProgress) {
    t2 = currentProgress && <div className="text-center space-y-3"><ProgressIcon stage={currentProgress.stage} className="mb-3" /><ProgressText message={decodeHtmlEntities(currentProgress.message || `${currentProgress.stage.charAt(0).toUpperCase() + currentProgress.stage.slice(1)} Wiki`)} percentage={currentProgress.progressPercent} />{currentProgress.progressPercent !== null && <ProgressBar percentage={currentProgress.progressPercent} />}</div>;
    $[1] = currentProgress;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onRetry) {
    t3 = onRetry && <div className="text-center"><WikiRetryButton onRetry={onRetry} isRetrying={false} /></div>;
    $[3] = onRetry;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className="w-full max-w-sm mx-auto space-y-4">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

// ==================== Generation Progress ====================

type GenerationProgressProps = {
  progress?: {
    stage?: string;
    message?: string;
    percentage?: number;
  } | null;
  currentPage?: string | null;
  estimatedTimeRemaining?: number | null;
  generatedPages?: string[];
  generationError?: string | null;
  onRetry?: () => void;
  isWikiPending?: boolean;
  wikiId?: string;
  useDatabase?: boolean;
};
export function GenerationProgress(t0) {
  const $ = _c(25);
  const {
    progress,
    currentPage,
    estimatedTimeRemaining,
    generatedPages: t1,
    generationError,
    onRetry,
    isWikiPending,
    wikiId,
    useDatabase: t2
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const generatedPages = t3;
  const useDatabase = t2 === undefined ? false : t2;
  if (useDatabase && wikiId) {
    let t4;
    if ($[2] !== onRetry || $[3] !== wikiId) {
      t4 = <WikiProgressDisplay wikiId={wikiId} onRetry={onRetry} isGenerating={true} />;
      $[2] = onRetry;
      $[3] = wikiId;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  let t4;
  if ($[5] !== currentPage || $[6] !== estimatedTimeRemaining || $[7] !== isWikiPending || $[8] !== progress) {
    t4 = progress && <div className="space-y-3"><div className="text-center"><ProgressIcon stage={progress.stage} className="mb-3" /><ProgressText message={progress.message || (isWikiPending ? "Processing wiki" : "Generating wiki")} percentage={progress.percentage} /></div>{progress.percentage !== undefined && <ProgressBar percentage={progress.percentage} />}{currentPage && progress.stage === WikiGenerationStage.page_generation && <div className="bg-muted/20 rounded-md p-3 border border-border/50"><div className="flex items-center gap-2 text-xs"><FileText className="h-3 w-3 text-muted-foreground" /><span className="text-muted-foreground">Generating:<span className="text-foreground/80 font-medium">{decodeHtmlEntities(currentPage)}</span></span></div></div>}{estimatedTimeRemaining && <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground"><Clock className="h-3 w-3" /><span>~{formatTimeRemaining(estimatedTimeRemaining)} remaining</span></div>}</div>;
    $[5] = currentPage;
    $[6] = estimatedTimeRemaining;
    $[7] = isWikiPending;
    $[8] = progress;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== generatedPages) {
    t5 = generatedPages.length > 0 && <div className="space-y-3"><h4 className="text-sm font-semibold text-center">Generated pages ({generatedPages.length})</h4><div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">{generatedPages.map(_temp)}</div></div>;
    $[10] = generatedPages;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== isWikiPending || $[13] !== progress) {
    t6 = isWikiPending && !progress && <div className="text-center space-y-3 w-full"><ProgressIcon stage={WikiGenerationStage.page_generation} /><ProgressText message="Processing your request" percentage={null} /><p className="text-xs text-muted-foreground -mt-2 max-w-xs mx-auto">We're generating comprehensive documentation for this repository. This may take a few minutes.</p></div>;
    $[12] = isWikiPending;
    $[13] = progress;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== generationError) {
    t7 = generationError && <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4"><div className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-destructive" /><span className="font-medium">Generation failed</span></div><p className="text-sm mt-1 text-muted-foreground">{generationError}</p></div>;
    $[15] = generationError;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== onRetry) {
    t8 = onRetry && <div className="text-center mt-6"><WikiRetryButton onRetry={onRetry} isRetrying={false} /></div>;
    $[17] = onRetry;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <div className="w-full max-w-sm mx-auto">{t4}{t5}{t6}{t7}{t8}</div>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
function _temp(page) {
  return <div key={page} className="flex items-center gap-2 text-sm bg-muted rounded-md p-2"><CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" /><span className="truncate">{decodeHtmlEntities(page)}</span></div>;
}